/**
 * One residential fee day, in integer cents (WS3 §2.3).
 *
 * No consumer in apps/web is switched in this slice. SQL parity is a later
 * mirror of this result shape.
 *
 * Rates are an input. This file does not look up a rate table. A null `rates`
 * row holds the day with `RATE_PERIOD_MISSING` and does not price it.
 *
 * Cap rule (contribution-caps F1, Act s 279(5)): the cap bites only when the
 * prior-day total already exceeded the lifetime cap on that trigger day. The
 * crossing day is charged in full. A day that lands exactly on the cap is
 * still charged the next day. There is no partial clamp. The comparison is
 * against the cap on the trigger day, not today's cap. `sharedLifetimeCapExceededOn`
 * records that day. Once set, NCCC stays zero even if a later rate period
 * raises the cap. An opening position may supply the date from Services
 * Australia advice. This calculator sets it when it sees the total cross
 * today's cap, or, when the opening total is already over today's cap and no
 * date was supplied, on the previous day.
 *
 * MTCF annual cap (Rules r 285A-14(2), contribution-caps F2, cap-carry F4):
 * one period of 12 months, starting on the first-entry day or the anniversary
 * of that day that falls in [2024-11-01, 2025-10-31]. It ends by 2026-10-30
 * and does not reset. On and after 2026-10-31 no annual cap applies. A
 * supplied `mtcfAnnualPeriodStart` without a first-entry day uses that single
 * period's 12-month bounds, still cut off on 2026-10-31. Neither fact, on a
 * day that could still fall in a period, holds `MTCF_ANNUAL_PERIOD_UNRESOLVABLE`
 * and does not apply an indefinite cap. A 29 February first entry with no
 * anniversary in the window holds the same way. No 1 March substitute is used.
 *
 * Shared lifetime total (cap-carry F1/F2): `sharedLifetimeTotalCents` is the
 * Act s 279(5) opening from the Services Australia advice. It already includes
 * Support at Home s 273, old-Act MTCF and old-Act ITF. This calculator adds
 * NCCC net, and for a post-2014 cohort resident also adds Rules MTCF net. It
 * never zeroes carried MTCF on an opt-in. `mtcfLifetimeTotalCents` is the
 * separate Rules r 285A-14(4) meter (Rules MTCF plus old-Act reductions).
 *
 * The 1,460-day counter (departure F3, s 279(4)(a)) counts a day when an NCCC
 * could have been charged, including a departure day the provider policy does
 * not bill and a day a fee reduction takes to zero (s 279(4)(b)).
 *
 * Fee reduction (Rules r 231-25): BDF, then hotelling, then NCCC, then
 * accommodation. Grandfathered MTCF is BDF, then MTCF, then accommodation
 * (Rules r 242B-27(6)). Never below zero. HELF and the bed reservation fee
 * are not in the order. Remainder is discarded and reported. The bed
 * reservation ceiling (Rules r 282-5, r 285A-20) uses the hypothetical
 * resident contribution after that waterfall, then adds the standard base
 * provider amount and, for a transitional resident, the as-if base rate.
 * The reduction is not applied again to the reservation fee. A missing
 * transitional base rate holds `BED_RESERVATION_BASE_RATE_MISSING`.
 *
 * A supplied rate row must cover `date`. A row outside its effective window
 * holds `RATE_PERIOD_MISMATCH` and is not priced. That check also covers a
 * row returned by a caller-supplied resolver.
 *
 * Departure (departure F1/F2): BDF and the accommodation payment (DAP, s 300)
 * are charged. Means-tested components follow the policy, default
 * NOT_CHARGED. Formula amount reasons are omitted on those days. Zero-class
 * and input-fact reasons stay, because they still describe the statutory
 * position.
 *
 * `retroBillable` is true only for means-pending when no statutory zero class
 * has forced every means component of that cohort to zero (dependent child,
 * former prisoner of war, s 280, r 280-5(a)(i)–(iii), r 285A-14(5)–(8)).
 *
 * Pre-1 July 2014 returns `UNSUPPORTED_PRE_2014` and does not price ITF or
 * the pre-2014 BDF variants. That work is C5.
 *
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-care-fee-formula-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-contribution-caps-trigger-day-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-cap-carry-composition-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-zero-contribution-classes-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-fee-reduction-supplement-order-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-departure-day-and-death-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-leave-day-counting-and-bed-reservation-primary-source-verification.md
 *
 * Low-means accommodation contribution (D7, Act s 298, Rules r 298-10). The
 * optional `accommodationContribution` carries the fee advice's accommodation
 * means class. ACCOMMODATION_PRICE (or no input) keeps `accommodationCents` as
 * the agreed accommodation payment. LOW_MEANS_CONTRIBUTION charges the daily
 * accommodation contribution (DAC): the advised maximum, or a lower agreed
 * daily amount, never above the advised maximum and never indexed. A missing
 * advised maximum holds `ACCOMMODATION_CONTRIBUTION_MAX_MISSING`. A refundable
 * contribution (RAC) balance holds `ACCOMMODATION_RAC_BALANCE_UNRESOLVED`: the
 * r 298-10(4) reduction needs the r 298-10(3) maximum refundable contribution,
 * which is not captured yet. FULLY_SUPPORTED charges no accommodation. The DAC
 * is the accommodation component, so it is last in the fee reduction order
 * (Rules r 231-25(d)).
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-accommodation-indexation-retention-refund-primary-source-verification.md
 */

import { daysInMonth, formatYmd, previousYmd } from '../residential-days';
import { RESIDENTIAL_FEE_COHORTS, type ResidentialFeeCohort } from './cohorts';
import { RESIDENTIAL_DAY_STATES, type ResidentialDayState } from './day-states';
import { RESIDENTIAL_COMPONENT_GST, type ResidentialGstTreatment } from './gst';
import {
  RAC_DAY_CALC_VERSION,
  resolveResidentialFeePolicy,
  type ResidentialFeePolicyV3,
} from './policy';
import {
  MTCF_UNDER_ONE_DOLLAR_CENTS,
  NCCC_TIME_LIMIT_DAYS,
  type ResidentialFeeRatePeriodV3,
  assertResidentialFeeDate,
  assertResidentialFeeRatePeriod,
} from './rates';
import { orderResidentialFeeReasonCodes, type ResidentialFeeReasonCode } from './reason-codes';

/** Amount-outcome codes. Dropped when the day does not charge means-tested fees. */
const FORMULA_AMOUNT_REASON_CODES = [
  'HOTELLING_BELOW_MAX',
  'HOTELLING_ZERO_DMTA_BELOW_MAS',
  'NCCC_ZERO_DMTA_BELOW_THRESHOLD',
  'NCCC_DAILY_MAX',
  'NCCC_1460_DAY_LIMIT',
  'NCCC_LIFETIME_CAP_EXCEEDED',
  'NCCC_LIFETIME_CAP_CROSSED_TODAY',
  'MTCF_COST_OF_CARE_CAP',
  'MTCF_UNDER_ONE_DOLLAR',
  'MTCF_ANNUAL_CAP_EXCEEDED',
  'MTCF_LIFETIME_CAP_EXCEEDED',
] as const satisfies readonly ResidentialFeeReasonCode[];

export interface ResidentialFeeMeansInput {
  /** Null means unknown. Zero is a real DMTA of $0, not "blank". */
  dailyMeansTestedAmountCents?: number | null;
  meansNotDisclosed?: boolean;
  /** Per-resident MTCF cap (r 285A-14). Not the published daily maximum. */
  residentCostOfCareCents?: number | null;
  /** Null means the advice did not state the component. Blank is not zero. */
  hotellingAdvisedCents?: number | null;
  ncccAdvisedCents?: number | null;
  mtcfAdvisedCents?: number | null;
  /** First old-Act entry day. Annual MTCF period anniversary. Not the stay start. */
  mtcfFirstEntryDay?: string | null;
  /**
   * Old-Act day of entry, if any (Rules r 285A-14(7)(b)). Not the annual-cap
   * first-entry day and not the current stay start.
   */
  oldActDayOfEntry?: string | null;
  /** Day the individual was informed of the means tested care fee. Rules r 285A-14(7). */
  mtcfInformedOn?: string | null;
  /** Day the person was told their DMTA. Rules r 280-5(a)(iii) only. */
  dmtaInformedOn?: string | null;
  /** Day the provider was told the contributions. Null can fall back to sourceIssuedAt. */
  providerInformedOn?: string | null;
  /** Tenant-zone date of the fee advice. Already a date, not a UTC timestamp. */
  sourceIssuedAt?: string | null;
  /** Stay start for the six-month zero rules. Not the old-Act first entry. */
  stayStartDay?: string | null;
  ceasedOn?: string | null;
  /**
   * Rules r 280-5(a)(i) and r 285A-14(8)(b). True only when the person ceased
   * this home and did not start at another approved home. A transfer is false.
   * The cessation zero classes do not apply unless this is true.
   */
  ceasedWithoutStartingAnotherHome?: boolean;
  diedBeforeProviderInformed?: boolean;
  zeroClassDependentChild?: boolean;
  zeroClassFormerPow?: boolean;
  zeroClassContinuingHcp?: boolean;
  ncccZeroPre2014OptOut?: boolean;
  /** System Governor determination in force on this day (Act s 280). NCCC cohort only. */
  zeroDeterminationS280?: boolean;
  /**
   * Advice column `mtcf_zero_class`. The row does not name the subclass.
   * True zeros the means-tested care fee for this day.
   */
  mtcfZeroClass?: boolean;
}

export interface ResidentialFeeMeans {
  dailyMeansTestedAmountCents: number | null;
  meansNotDisclosed: boolean;
  residentCostOfCareCents: number | null;
  hotellingAdvisedCents: number | null;
  ncccAdvisedCents: number | null;
  mtcfAdvisedCents: number | null;
  mtcfFirstEntryDay: string | null;
  oldActDayOfEntry: string | null;
  mtcfInformedOn: string | null;
  dmtaInformedOn: string | null;
  providerInformedOn: string | null;
  sourceIssuedAt: string | null;
  stayStartDay: string | null;
  ceasedOn: string | null;
  ceasedWithoutStartingAnotherHome: boolean;
  diedBeforeProviderInformed: boolean;
  zeroClassDependentChild: boolean;
  zeroClassFormerPow: boolean;
  zeroClassContinuingHcp: boolean;
  ncccZeroPre2014OptOut: boolean;
  zeroDeterminationS280: boolean;
  mtcfZeroClass: boolean;
}

export interface ResidentialFeePositions {
  /**
   * Act s 279(5) shared lifetime total. This is an opening position from the
   * Services Australia advice. That advice already includes Support at Home
   * s 273 contributions, old-Act means tested care fees, and old-Act income
   * tested fees. This calculator adds the NCCC net. For a post-2014 cohort
   * resident it also adds the Rules means tested care fee net (r 279-15).
   * An opt-in to NCCC must pass the carried total here. It is never zeroed.
   */
  sharedLifetimeTotalCents: number;
  /**
   * Means tested care fees during the single Rules r 285A-14(2) annual cap
   * period. Incremented only on a day inside that period. Not reset.
   */
  mtcfAnnualTotalCents: number;
  /**
   * Separate Rules r 285A-14(4) meter. The opening is Rules MTCF plus the
   * old-Act ss 44-21 and 48-7 combined care subsidy reductions supplied with
   * the Services Australia advice. This calculator adds Rules MTCF net only.
   * It does not include Support at Home contributions or NCCC.
   */
  mtcfLifetimeTotalCents: number;
  /**
   * Days on which an NCCC could have been charged (Act s 279(4)(a)), including
   * a departure day billed under the not-charged policy and a fee-reduction
   * day (s 279(4)(b)). A day whose statutory NCCC is already zero does not count.
   */
  ncccDays: number;
  /**
   * Start of the single MTCF annual cap period derived from the first-entry
   * day. Null when that resident has no such period. When the first-entry day
   * is absent, a supplied start is that one period's 12-month bound.
   */
  mtcfAnnualPeriodStart: string | null;
  /**
   * Act s 279(5) trigger day. The shared lifetime total exceeded the lifetime
   * cap on this day. Null until that happens. Once set, NCCC stays zero.
   */
  sharedLifetimeCapExceededOn: string | null;
}

export interface ResidentialFeeCapOverrides {
  ncccLifetimeCapCents?: number;
  mtcfAnnualCapCents?: number;
  mtcfLifetimeCapCents?: number;
  ncccTimeLimitDays?: number;
}

export interface ResidentialBedReservationInput {
  agreementId?: string;
  gstTreatment?: ResidentialGstTreatment;
  /** Null means there is no agreement. Zero means an agreement to charge nothing. */
  agreedDailyCents: number | null;
  /** AN-ACC base rate "as if" delivered. Transitional bed reservation only (r 285A-20). */
  baseRateAsIfCents?: number;
  /** Overrides the rate-row standard base provider amount for this day. */
  standardBaseProviderAmountCents?: number;
}

export const RESIDENTIAL_ACCOMMODATION_MEANS_CLASSES = [
  'ACCOMMODATION_PRICE',
  'LOW_MEANS_CONTRIBUTION',
  'FULLY_SUPPORTED',
] as const;

export type ResidentialAccommodationMeansClass =
  (typeof RESIDENTIAL_ACCOMMODATION_MEANS_CLASSES)[number];

/** D7: accommodation means class from the active V2 fee advice (Act s 298, Rules r 298-10). */
export interface ResidentialAccommodationContributionInput {
  meansClass: ResidentialAccommodationMeansClass;
  /** Advised maximum daily accommodation contribution. Required for LOW_MEANS_CONTRIBUTION. */
  advisedMaxDailyCents?: number | null;
  /** A lower daily contribution agreed with the resident. Null charges the advised maximum. */
  agreedDailyCents?: number | null;
  /** Refundable contribution (RAC) balance on the day. Positive holds the day (r 298-10(4)). */
  refundableBalanceCents?: number | null;
}

export interface ResidentialFeeDayInput {
  date: string;
  cohort: ResidentialFeeCohort;
  dayState: ResidentialDayState;
  socialLeaveOrdinalFy?: number | null;
  hospitalEpisodeDay?: number | null;
  means?: ResidentialFeeMeansInput | null;
  positionsBefore?: Partial<ResidentialFeePositions> | null;
  accommodationCents?: number;
  /**
   * D7. LOW_MEANS_CONTRIBUTION and FULLY_SUPPORTED set the accommodation
   * amount, so `accommodationCents` must then be 0 or omitted.
   */
  accommodationContribution?: ResidentialAccommodationContributionInput | null;
  feeReductionCents?: number;
  helfCents?: number;
  helfAgreementIds?: readonly string[];
  helfGstTreatment?: ResidentialGstTreatment;
  bedReservation?: ResidentialBedReservationInput | null;
  policy?: Partial<ResidentialFeePolicyV3> | null;
  caps?: ResidentialFeeCapOverrides | null;
  /**
   * When omitted, the effective-from date of the rate period is an indexation
   * day and advice mismatches on that day do not hold the resident.
   */
  indexationDay?: boolean;
  /**
   * The rate period row for this date. Null holds the day with
   * `RATE_PERIOD_MISSING`. This function does not look up a rate table.
   */
  rates: ResidentialFeeRatePeriodV3 | null;
}

export interface ResidentialFeeComponentCents {
  grossCents: number;
  reductionCents: number;
  netCents: number;
  gstTreatment: ResidentialGstTreatment;
}

export interface ResidentialFeeDayResult {
  date: string;
  dayState: ResidentialDayState;
  cohort: ResidentialFeeCohort;
  calcVersion: typeof RAC_DAY_CALC_VERSION;
  ratePeriodId: string | null;
  supported: boolean;
  hold: boolean;
  retroBillable: boolean;
  socialLeaveOrdinalFy: number | null;
  hospitalEpisodeDay: number | null;
  bdf: ResidentialFeeComponentCents;
  hotelling: ResidentialFeeComponentCents;
  nccc: ResidentialFeeComponentCents;
  mtcf: ResidentialFeeComponentCents;
  itf: ResidentialFeeComponentCents;
  accommodation: ResidentialFeeComponentCents;
  bedReservation: ResidentialFeeComponentCents;
  helf: ResidentialFeeComponentCents;
  /** Statutory bed-reservation ceiling for this day, or null when the state is not a bed reservation. */
  maxBedReservationCents: number | null;
  feeReductionAvailableCents: number;
  feeReductionAppliedCents: number;
  feeReductionUnappliedCents: number;
  ncccDayIncrement: 0 | 1;
  /** Added to `sharedLifetimeTotalCents`. NCCC net, or Rules MTCF net for the post-2014 cohort. */
  sharedLifetimeIncrementCents: number;
  mtcfAnnualIncrementCents: number;
  mtcfLifetimeIncrementCents: number;
  mtcfAnnualPeriodStart: string | null;
  positionsOpen: ResidentialFeePositions;
  positionsClose: ResidentialFeePositions;
  reasonCodes: ResidentialFeeReasonCode[];
  careNetCents: number;
  grossDailyTotalCents: number;
}

export function emptyResidentialFeePositions(): ResidentialFeePositions {
  return {
    sharedLifetimeTotalCents: 0,
    mtcfAnnualTotalCents: 0,
    mtcfLifetimeTotalCents: 0,
    ncccDays: 0,
    mtcfAnnualPeriodStart: null,
    sharedLifetimeCapExceededOn: null,
  };
}

/**
 * The 12 months before the 1 November 2025 transition. Rules r 285A-14(2)
 * places the single MTCF annual cap period on the first-entry day, or its
 * anniversary, that falls in this window.
 */
export const MTCF_ANNUAL_CAP_WINDOW_START = '2024-11-01';
export const MTCF_ANNUAL_CAP_WINDOW_END = '2025-10-31';
/** Last day any r 285A-14(2) annual cap can apply. The next day, 2026-10-31, never has one. */
export const MTCF_ANNUAL_CAP_LAST_DAY = '2026-10-30';

export type MtcfAnnualCapPeriod =
  | { status: 'period'; start: string; endInclusive: string }
  | { status: 'none' }
  | { status: 'unresolvable' };

export function addCalendarMonths(date: string, months: number): string {
  assertResidentialFeeDate(date);
  if (!Number.isInteger(months)) throw new RangeError('months must be an integer');
  const year = Number(date.slice(0, 4));
  const monthIndex = Number(date.slice(5, 7)) - 1;
  const day = Number(date.slice(8, 10));
  const absolute = monthIndex + months;
  const targetYear = year + Math.floor(absolute / 12);
  const targetMonthIndex = ((absolute % 12) + 12) % 12;
  const dim = daysInMonth(targetYear, targetMonthIndex + 1);
  const targetDay = day < dim ? day : dim;
  return formatYmd(targetYear, targetMonthIndex + 1, targetDay);
}

/**
 * The single Rules r 285A-14(2) annual cap period for a first-entry day.
 * A 29 February first entry with no anniversary inside the window is
 * `unresolvable`. A first entry after 31 October 2025 has no period (`none`).
 */
export function resolveMtcfAnnualCapPeriod(firstEntryDay: string): MtcfAnnualCapPeriod {
  assertResidentialFeeDate(firstEntryDay);
  if (firstEntryDay > MTCF_ANNUAL_CAP_WINDOW_END) return { status: 'none' };
  const start =
    firstEntryDay >= MTCF_ANNUAL_CAP_WINDOW_START
      ? firstEntryDay
      : anniversaryInsideAnnualWindow(firstEntryDay);
  if (start === null) return { status: 'unresolvable' };
  const exclusiveEnd = addCalendarMonths(start, 12);
  return { status: 'period', start, endInclusive: previousYmd(exclusiveEnd) };
}

export function normalizeResidentialFeeMeans(
  input?: ResidentialFeeMeansInput | null,
): ResidentialFeeMeans {
  const means: ResidentialFeeMeans = {
    dailyMeansTestedAmountCents: input?.dailyMeansTestedAmountCents ?? null,
    meansNotDisclosed: input?.meansNotDisclosed ?? false,
    residentCostOfCareCents: input?.residentCostOfCareCents ?? null,
    hotellingAdvisedCents: input?.hotellingAdvisedCents ?? null,
    ncccAdvisedCents: input?.ncccAdvisedCents ?? null,
    mtcfAdvisedCents: input?.mtcfAdvisedCents ?? null,
    mtcfFirstEntryDay: optionalDate('mtcfFirstEntryDay', input?.mtcfFirstEntryDay),
    oldActDayOfEntry: optionalDate('oldActDayOfEntry', input?.oldActDayOfEntry),
    mtcfInformedOn: optionalDate('mtcfInformedOn', input?.mtcfInformedOn),
    dmtaInformedOn: optionalDate('dmtaInformedOn', input?.dmtaInformedOn),
    providerInformedOn: optionalDate('providerInformedOn', input?.providerInformedOn),
    sourceIssuedAt: optionalDate('sourceIssuedAt', input?.sourceIssuedAt),
    stayStartDay: optionalDate('stayStartDay', input?.stayStartDay),
    ceasedOn: optionalDate('ceasedOn', input?.ceasedOn),
    ceasedWithoutStartingAnotherHome: input?.ceasedWithoutStartingAnotherHome ?? false,
    diedBeforeProviderInformed: input?.diedBeforeProviderInformed ?? false,
    zeroClassDependentChild: input?.zeroClassDependentChild ?? false,
    zeroClassFormerPow: input?.zeroClassFormerPow ?? false,
    zeroClassContinuingHcp: input?.zeroClassContinuingHcp ?? false,
    ncccZeroPre2014OptOut: input?.ncccZeroPre2014OptOut ?? false,
    zeroDeterminationS280: input?.zeroDeterminationS280 ?? false,
    mtcfZeroClass: input?.mtcfZeroClass ?? false,
  };
  optionalCents('dailyMeansTestedAmountCents', means.dailyMeansTestedAmountCents);
  optionalCents('residentCostOfCareCents', means.residentCostOfCareCents);
  optionalCents('hotellingAdvisedCents', means.hotellingAdvisedCents);
  optionalCents('ncccAdvisedCents', means.ncccAdvisedCents);
  optionalCents('mtcfAdvisedCents', means.mtcfAdvisedCents);
  return means;
}

export function computeResidentialFeeDay(input: ResidentialFeeDayInput): ResidentialFeeDayResult {
  assertResidentialFeeDate(input.date);
  assertCohort(input.cohort);
  assertDayState(input.dayState);
  const policy = resolveResidentialFeePolicy(input.policy);
  const means = normalizeResidentialFeeMeans(input.means);
  const supplied = normalizePositions(input.positionsBefore);
  const suppliedAccommodationCents = nonNegativeCents(
    'accommodationCents',
    input.accommodationCents ?? 0,
  );
  const contribution = normalizeAccommodationContribution(
    input.accommodationContribution,
    suppliedAccommodationCents,
  );
  const feeReductionCents = nonNegativeCents('feeReductionCents', input.feeReductionCents ?? 0);
  const helfCents = nonNegativeCents('helfCents', input.helfCents ?? 0);
  const socialLeaveOrdinalFy = input.socialLeaveOrdinalFy ?? null;
  const hospitalEpisodeDay = input.hospitalEpisodeDay ?? null;

  if (input.rates !== null) {
    assertResidentialFeeRatePeriod(input.rates);
    if (!rateWindowCovers(input.rates, input.date)) {
      return ratePeriodRejected({
        date: input.date,
        dayState: input.dayState,
        cohort: input.cohort,
        socialLeaveOrdinalFy,
        hospitalEpisodeDay,
        positions: supplied,
        feeReductionCents,
        ratePeriodId: input.rates.id,
        supported: input.cohort !== 'ITF_PRE_2014',
      });
    }
  }

  if (input.cohort === 'ITF_PRE_2014') {
    return unsupportedPre2014({
      date: input.date,
      dayState: input.dayState,
      socialLeaveOrdinalFy,
      hospitalEpisodeDay,
      positions: supplied,
      feeReductionCents,
      rates: input.rates,
    });
  }

  if (input.rates === null) {
    return ratePeriodMissing({
      date: input.date,
      dayState: input.dayState,
      cohort: input.cohort,
      socialLeaveOrdinalFy,
      hospitalEpisodeDay,
      positions: supplied,
      feeReductionCents,
    });
  }

  const rateRow = input.rates;
  const reasons = new Set<ResidentialFeeReasonCode>(['RATE_PERIOD_APPLIED']);
  addDayStateReason(reasons, input.dayState);

  const annual = resolveAnnualPeriod(input.cohort, means.mtcfFirstEntryDay, input.date, supplied);
  if (annual.unresolvable) reasons.add('MTCF_ANNUAL_PERIOD_UNRESOLVABLE');
  if (annual.ended) reasons.add('MTCF_ANNUAL_CAP_PERIOD_ENDED');

  const providerInformed = resolveProviderInformed(means, policy);
  if (providerInformed.assumed) reasons.add('PROVIDER_INFORMED_ON_ASSUMED_FROM_SOURCE_ISSUED_AT');

  const caps = resolveCaps(rateRow, input.caps);
  const formula =
    input.cohort === 'NCCC_2025'
      ? deriveNccc({
          rateRow,
          means,
          providerInformedOn: providerInformed.date,
          lifetimeBeforeCents: supplied.sharedLifetimeTotalCents,
          capExceededOn: supplied.sharedLifetimeCapExceededOn,
          ncccDaysBefore: supplied.ncccDays,
          caps,
          reasons,
        })
      : deriveMtcf({
          rateRow,
          means,
          providerInformedOn: providerInformed.date,
          date: input.date,
          annualBeforeCents: annual.annualBeforeCents,
          annualCapApplies: annual.capApplies,
          lifetimeBeforeCents: supplied.mtcfLifetimeTotalCents,
          caps,
          reasons,
        });

  if (
    input.cohort === 'MTCF_POST_2014' &&
    means.hotellingAdvisedCents !== null &&
    means.hotellingAdvisedCents > 0
  ) {
    reasons.add('HOTELLING_NOT_APPLICABLE_TRANSITIONAL');
  }

  const meansAreChargeable = meansTestedChargeable(input.dayState, policy);
  if (input.dayState === 'DEPARTURE' && policy.departureDayMeansTested === 'NOT_CHARGED') {
    reasons.add('DEPARTURE_MEANS_TESTED_NOT_CHARGED_POLICY');
  }

  const bdfGrossCents = input.dayState === 'BED_RESERVATION' ? 0 : rateRow.bdfCents;
  const hotellingGrossCents = meansAreChargeable ? formula.hotellingCents : 0;
  const ncccGrossCents = meansAreChargeable ? formula.ncccCents : 0;
  const mtcfGrossCents = meansAreChargeable ? formula.mtcfCents : 0;

  const indexationDay = input.indexationDay ?? input.date === rateRow.effectiveFrom;
  let hold = annual.unresolvable || formula.lateInformationUnresolvable;
  const accommodationResolved = resolveAccommodationCents(suppliedAccommodationCents, contribution);
  for (const code of accommodationResolved.reasons) reasons.add(code);
  if (accommodationResolved.hold) hold = true;
  const accommodationCents = accommodationResolved.cents;
  if (
    policy.requireMtcfFirstEntryDay === true &&
    input.cohort === 'MTCF_POST_2014' &&
    means.mtcfFirstEntryDay === null
  ) {
    reasons.add('MTCF_FIRST_ENTRY_DAY_MISSING');
    hold = true;
  }
  if (!indexationDay && adviceMismatches(input.cohort, means, formula)) {
    reasons.add('ADVICE_DERIVATION_MISMATCH');
    hold = true;
  }

  let bedReservationGrossCents = 0;
  let maxBedReservationCents: number | null = null;
  if (input.dayState === 'BED_RESERVATION') {
    const hypotheticalContributionCents = contributionAfterReduction({
      cohort: input.cohort,
      bdfCents: rateRow.bdfCents,
      hotellingCents: formula.hotellingCents,
      ncccCents: formula.ncccCents,
      mtcfCents: formula.mtcfCents,
      accommodationCents,
      feeReductionCents,
    });
    const priced = priceBedReservation({
      cohort: input.cohort,
      rateRow,
      bedReservation: input.bedReservation ?? null,
      hypotheticalContributionCents,
    });
    bedReservationGrossCents = priced.feeCents;
    maxBedReservationCents = priced.maxCents;
    if (priced.missingBaseRate) {
      reasons.add('BED_RESERVATION_BASE_RATE_MISSING');
      hold = true;
    }
    reasons.add(priced.agreed ? 'BED_RESERVATION_FEE_AGREED' : 'BED_RESERVATION_NO_AGREEMENT');
  }

  const bdf = component(bdfGrossCents, 'bdf');
  const hotelling = component(hotellingGrossCents, 'hotelling');
  const nccc = component(ncccGrossCents, 'nccc');
  const mtcf = component(mtcfGrossCents, 'mtcf');
  const itf = component(0, 'itf');
  const accommodation = component(accommodationCents, 'accommodation');
  const bedReservation = component(bedReservationGrossCents, 'bedReservation');
  const helf = component(helfCents, 'helf');
  if (input.helfGstTreatment === 'GST_FREE_RESIDENTIAL_CARE' && input.helfAgreementIds?.length)
    helf.gstTreatment = input.helfGstTreatment;
  if (
    input.bedReservation?.gstTreatment === 'GST_FREE_RESIDENTIAL_CARE' &&
    input.bedReservation.agreementId
  )
    bedReservation.gstTreatment = input.bedReservation.gstTreatment;
  const reductionOrder =
    input.cohort === 'MTCF_POST_2014'
      ? [bdf, mtcf, accommodation]
      : [bdf, hotelling, nccc, accommodation];
  const reduction = applyFeeReduction(reductionOrder, feeReductionCents);
  if (reduction.appliedCents > 0) reasons.add('FEE_REDUCTION_APPLIED');
  if (reduction.unappliedCents > 0) reasons.add('FEE_REDUCTION_UNAPPLIED_REMAINDER');

  const ncccDayIncrement: 0 | 1 =
    input.dayState !== 'BED_RESERVATION' && formula.ncccCents > 0 ? 1 : 0;
  const mtcfLifetimeIncrementCents = input.cohort === 'MTCF_POST_2014' ? mtcf.netCents : 0;
  const mtcfAnnualIncrementCents = annual.capApplies ? mtcfLifetimeIncrementCents : 0;
  const sharedLifetimeIncrementCents =
    input.cohort === 'NCCC_2025'
      ? nccc.netCents
      : input.cohort === 'MTCF_POST_2014'
        ? mtcf.netCents
        : 0;
  const positionsOpen: ResidentialFeePositions = {
    sharedLifetimeTotalCents: supplied.sharedLifetimeTotalCents,
    mtcfAnnualTotalCents: annual.annualBeforeCents,
    mtcfLifetimeTotalCents: supplied.mtcfLifetimeTotalCents,
    ncccDays: supplied.ncccDays,
    mtcfAnnualPeriodStart: annual.periodStart,
    sharedLifetimeCapExceededOn: supplied.sharedLifetimeCapExceededOn,
  };
  const positionsClose: ResidentialFeePositions = {
    sharedLifetimeTotalCents: positionsOpen.sharedLifetimeTotalCents + sharedLifetimeIncrementCents,
    mtcfAnnualTotalCents: positionsOpen.mtcfAnnualTotalCents + mtcfAnnualIncrementCents,
    mtcfLifetimeTotalCents: positionsOpen.mtcfLifetimeTotalCents + mtcfLifetimeIncrementCents,
    ncccDays: positionsOpen.ncccDays + ncccDayIncrement,
    mtcfAnnualPeriodStart: annual.periodStart,
    sharedLifetimeCapExceededOn: nextLifetimeCapExceededOn({
      openOn: supplied.sharedLifetimeCapExceededOn,
      openTotalCents: supplied.sharedLifetimeTotalCents,
      closeTotalCents: supplied.sharedLifetimeTotalCents + sharedLifetimeIncrementCents,
      capCents: caps.ncccLifetimeCapCents,
      date: input.date,
    }),
  };
  const careNetCents =
    bdf.netCents + hotelling.netCents + nccc.netCents + mtcf.netCents + itf.netCents;
  if (!meansAreChargeable) {
    for (const code of FORMULA_AMOUNT_REASON_CODES) reasons.delete(code);
  }

  return {
    date: input.date,
    dayState: input.dayState,
    cohort: input.cohort,
    calcVersion: RAC_DAY_CALC_VERSION,
    ratePeriodId: rateRow.id,
    supported: true,
    hold,
    retroBillable: formula.pending && !formula.meansForcedZero,
    socialLeaveOrdinalFy,
    hospitalEpisodeDay,
    bdf,
    hotelling,
    nccc,
    mtcf,
    itf,
    accommodation,
    bedReservation,
    helf,
    maxBedReservationCents,
    feeReductionAvailableCents: feeReductionCents,
    feeReductionAppliedCents: reduction.appliedCents,
    feeReductionUnappliedCents: reduction.unappliedCents,
    ncccDayIncrement,
    sharedLifetimeIncrementCents,
    mtcfAnnualIncrementCents,
    mtcfLifetimeIncrementCents,
    mtcfAnnualPeriodStart: annual.periodStart,
    positionsOpen,
    positionsClose,
    reasonCodes: orderResidentialFeeReasonCodes(reasons),
    careNetCents,
    grossDailyTotalCents:
      careNetCents + accommodation.netCents + bedReservation.netCents + helf.netCents,
  };
}

interface FormulaAmounts {
  hotellingCents: number;
  ncccCents: number;
  mtcfCents: number;
  pending: boolean;
  /** A statutory class has already forced this cohort's means components to zero. */
  meansForcedZero: boolean;
  /** Rules r 285A-14(7) was raised but the applicable day or the informed date is missing. */
  lateInformationUnresolvable: boolean;
}

function deriveNccc(args: {
  rateRow: ResidentialFeeRatePeriodV3;
  means: ResidentialFeeMeans;
  providerInformedOn: string | null;
  lifetimeBeforeCents: number;
  /** Act s 279(5) trigger day already recorded. Null when this stay has not crossed. */
  capExceededOn: string | null;
  ncccDaysBefore: number;
  caps: ResolvedCaps;
  reasons: Set<ResidentialFeeReasonCode>;
}): FormulaAmounts {
  const { rateRow, means, reasons } = args;
  let hotellingCents = 0;
  let ncccCents = 0;
  let pending = false;

  if (means.meansNotDisclosed) {
    hotellingCents = rateRow.mhsCents;
    ncccCents = rateRow.mncCents;
    reasons.add('MEANS_NOT_DISCLOSED');
  } else if (means.dailyMeansTestedAmountCents === null) {
    pending = true;
    reasons.add('MEANS_PENDING_ADVICE');
  } else {
    const dmtaCents = means.dailyMeansTestedAmountCents;
    const aboveMasCents = dmtaCents - rateRow.masCents;
    if (aboveMasCents <= 0) {
      hotellingCents = 0;
      reasons.add('HOTELLING_ZERO_DMTA_BELOW_MAS');
    } else if (aboveMasCents < rateRow.mhsCents) {
      hotellingCents = aboveMasCents;
      reasons.add('HOTELLING_BELOW_MAX');
    } else {
      hotellingCents = rateRow.mhsCents;
    }
    const thresholdCents = rateRow.masCents + rateRow.mhsCents;
    if (dmtaCents > thresholdCents) {
      const rawCents = dmtaCents - thresholdCents;
      if (rawCents > rateRow.mncCents) {
        ncccCents = rateRow.mncCents;
        reasons.add('NCCC_DAILY_MAX');
      } else {
        ncccCents = rawCents;
      }
    } else {
      ncccCents = 0;
      reasons.add('NCCC_ZERO_DMTA_BELOW_THRESHOLD');
    }
  }

  const meansForcedZero = applyNcccZeroClasses(
    means,
    args.providerInformedOn,
    reasons,
    () => {
      hotellingCents = 0;
      ncccCents = 0;
    },
    () => {
      ncccCents = 0;
    },
  );

  if (ncccCents > 0 && args.ncccDaysBefore >= args.caps.ncccTimeLimitDays) {
    ncccCents = 0;
    reasons.add('NCCC_1460_DAY_LIMIT');
  }
  // s 279(5) tests the cap on the trigger day. A later, higher cap does not restart NCCC.
  if (
    ncccCents > 0 &&
    (args.capExceededOn !== null || args.lifetimeBeforeCents > args.caps.ncccLifetimeCapCents)
  ) {
    ncccCents = 0;
    reasons.add('NCCC_LIFETIME_CAP_EXCEEDED');
  }
  if (ncccCents > 0 && args.lifetimeBeforeCents + ncccCents > args.caps.ncccLifetimeCapCents) {
    reasons.add('NCCC_LIFETIME_CAP_CROSSED_TODAY');
  }
  if (ncccCents === 0) reasons.delete('NCCC_DAILY_MAX');

  return {
    hotellingCents,
    ncccCents,
    mtcfCents: 0,
    pending,
    meansForcedZero,
    lateInformationUnresolvable: false,
  };
}

function deriveMtcf(args: {
  rateRow: ResidentialFeeRatePeriodV3;
  means: ResidentialFeeMeans;
  providerInformedOn: string | null;
  date: string;
  annualBeforeCents: number;
  annualCapApplies: boolean;
  lifetimeBeforeCents: number;
  caps: ResolvedCaps;
  reasons: Set<ResidentialFeeReasonCode>;
}): FormulaAmounts {
  const { rateRow, means, reasons } = args;
  const unconditional = applyMtcfUnconditionalZeros(
    means,
    args.date,
    args.providerInformedOn,
    reasons,
  );
  if (unconditional.zero) {
    return {
      hotellingCents: 0,
      ncccCents: 0,
      mtcfCents: 0,
      pending: false,
      meansForcedZero: true,
      lateInformationUnresolvable: false,
    };
  }

  let mtcfCents = 0;
  let pending = false;
  if (unconditional.lateUnresolvable) reasons.add('MTCF_LATE_INFORMATION_UNRESOLVABLE');

  if (means.meansNotDisclosed) {
    reasons.add('MEANS_NOT_DISCLOSED');
    mtcfCents = requiredCost(means);
  } else if (means.dailyMeansTestedAmountCents === null) {
    pending = true;
    reasons.add('MEANS_PENDING_ADVICE');
  } else {
    const step2Cents = means.dailyMeansTestedAmountCents - rateRow.masCents;
    if (step2Cents > 0) {
      const costCents = requiredCost(means);
      mtcfCents = step2Cents < costCents ? step2Cents : costCents;
      if (step2Cents > costCents) reasons.add('MTCF_COST_OF_CARE_CAP');
    }
  }

  if (mtcfCents > 0 && mtcfCents < MTCF_UNDER_ONE_DOLLAR_CENTS) {
    mtcfCents = 0;
    reasons.add('MTCF_UNDER_ONE_DOLLAR');
    reasons.delete('MTCF_COST_OF_CARE_CAP');
  }

  if (
    args.annualCapApplies &&
    mtcfCents > 0 &&
    args.annualBeforeCents > args.caps.mtcfAnnualCapCents
  ) {
    mtcfCents = 0;
    reasons.add('MTCF_ANNUAL_CAP_EXCEEDED');
  }
  if (mtcfCents > 0 && args.lifetimeBeforeCents > args.caps.mtcfLifetimeCapCents) {
    mtcfCents = 0;
    reasons.add('MTCF_LIFETIME_CAP_EXCEEDED');
  }
  if (mtcfCents === 0) reasons.delete('MTCF_COST_OF_CARE_CAP');

  return {
    hotellingCents: 0,
    ncccCents: 0,
    mtcfCents,
    pending,
    meansForcedZero: false,
    lateInformationUnresolvable: unconditional.lateUnresolvable,
  };
}

function applyNcccZeroClasses(
  means: ResidentialFeeMeans,
  providerInformedOn: string | null,
  reasons: Set<ResidentialFeeReasonCode>,
  wipeBoth: () => void,
  wipeNcccOnly: () => void,
): boolean {
  let wipesBoth = false;
  let wipesNccc = false;
  if (means.zeroClassDependentChild) {
    reasons.add('ZERO_CLASS_DEPENDENT_CHILD');
    wipesBoth = true;
  }
  if (means.zeroClassFormerPow) {
    reasons.add('ZERO_CLASS_FORMER_POW');
    wipesBoth = true;
  }
  if (means.diedBeforeProviderInformed) {
    reasons.add('ZERO_CLASS_DIED_BEFORE_INFORMED');
    wipesBoth = true;
  } else if (
    means.ceasedWithoutStartingAnotherHome &&
    ceasedBeforeProviderInformed(means.ceasedOn, providerInformedOn)
  ) {
    reasons.add('ZERO_CLASS_CEASED_BEFORE_INFORMED');
    wipesBoth = true;
  }
  if (ncccInformedAfterSixMonths(means.stayStartDay, means.dmtaInformedOn)) {
    reasons.add('ZERO_CLASS_INFORMED_AFTER_6_MONTHS');
    wipesBoth = true;
  }
  if (means.zeroDeterminationS280) {
    reasons.add('ZERO_DETERMINATION_S280');
    wipesBoth = true;
  }
  if (means.ncccZeroPre2014OptOut) {
    reasons.add('ZERO_CLASS_PRE2014_OPT_OUT');
    wipesNccc = true;
  }
  if (means.zeroClassContinuingHcp) {
    reasons.add('ZERO_CLASS_CONTINUING_HCP');
    wipesNccc = true;
  }
  if (wipesBoth) {
    wipeBoth();
    reasons.delete('HOTELLING_BELOW_MAX');
    reasons.delete('HOTELLING_ZERO_DMTA_BELOW_MAS');
    reasons.delete('NCCC_DAILY_MAX');
    reasons.delete('NCCC_ZERO_DMTA_BELOW_THRESHOLD');
  } else if (wipesNccc) {
    wipeNcccOnly();
    reasons.delete('NCCC_DAILY_MAX');
    reasons.delete('NCCC_ZERO_DMTA_BELOW_THRESHOLD');
  }
  return wipesBoth;
}

/**
 * Zeros that do not need a worked-out fee. Cost of care is not read here.
 * Late-information facts that are only partly supplied hold instead of zeroing.
 */
function applyMtcfUnconditionalZeros(
  means: ResidentialFeeMeans,
  date: string,
  providerInformedOn: string | null,
  reasons: Set<ResidentialFeeReasonCode>,
): { zero: boolean; lateUnresolvable: boolean } {
  let zero = false;
  if (means.mtcfZeroClass) {
    reasons.add('MTCF_ZERO_CLASS');
    zero = true;
  }
  if (means.zeroClassDependentChild) {
    reasons.add('ZERO_CLASS_DEPENDENT_CHILD');
    zero = true;
  }
  if (means.zeroClassFormerPow) {
    reasons.add('ZERO_CLASS_FORMER_POW');
    zero = true;
  }
  if (means.zeroClassContinuingHcp) {
    reasons.add('ZERO_CLASS_CONTINUING_HCP');
    zero = true;
  }
  if (means.diedBeforeProviderInformed) {
    reasons.add('ZERO_CLASS_DIED_BEFORE_INFORMED');
    zero = true;
  } else if (
    means.ceasedWithoutStartingAnotherHome &&
    ceasedBeforeProviderInformed(means.ceasedOn, providerInformedOn)
  ) {
    reasons.add('ZERO_CLASS_CEASED_BEFORE_INFORMED');
    zero = true;
  }

  const late = resolveMtcfLateInformation(date, means);
  if (late === 'zero') {
    reasons.add('ZERO_CLASS_INFORMED_AFTER_6_MONTHS');
    zero = true;
  }
  if (zero) {
    reasons.delete('MTCF_UNDER_ONE_DOLLAR');
    reasons.delete('MTCF_COST_OF_CARE_CAP');
  }
  return { zero, lateUnresolvable: late === 'unresolvable' };
}

function adviceMismatches(
  cohort: ResidentialFeeCohort,
  means: ResidentialFeeMeans,
  formula: FormulaAmounts,
): boolean {
  if (cohort === 'NCCC_2025') {
    return (
      advisedDiffers(means.hotellingAdvisedCents, formula.hotellingCents) ||
      advisedDiffers(means.ncccAdvisedCents, formula.ncccCents)
    );
  }
  return advisedDiffers(means.mtcfAdvisedCents, formula.mtcfCents);
}

function advisedDiffers(advisedCents: number | null, derivedCents: number): boolean {
  return advisedCents !== null && advisedCents !== derivedCents;
}

interface NormalizedAccommodationContribution {
  meansClass: ResidentialAccommodationMeansClass;
  advisedMaxDailyCents: number | null;
  agreedDailyCents: number | null;
  refundableBalanceCents: number;
}

function normalizeAccommodationContribution(
  input: ResidentialAccommodationContributionInput | null | undefined,
  suppliedAccommodationCents: number,
): NormalizedAccommodationContribution | null {
  if (input === null || input === undefined) return null;
  if (!(RESIDENTIAL_ACCOMMODATION_MEANS_CLASSES as readonly string[]).includes(input.meansClass)) {
    throw new RangeError(`Unsupported accommodation means class: ${String(input.meansClass)}`);
  }
  const normalized: NormalizedAccommodationContribution = {
    meansClass: input.meansClass,
    advisedMaxDailyCents: input.advisedMaxDailyCents ?? null,
    agreedDailyCents: input.agreedDailyCents ?? null,
    refundableBalanceCents: input.refundableBalanceCents ?? 0,
  };
  optionalCents('advisedMaxDailyCents', normalized.advisedMaxDailyCents);
  optionalCents('agreedDailyCents', normalized.agreedDailyCents);
  nonNegativeCents('refundableBalanceCents', normalized.refundableBalanceCents);
  if (normalized.meansClass !== 'ACCOMMODATION_PRICE' && suppliedAccommodationCents !== 0) {
    throw new RangeError(
      'accommodationCents must be 0 when the accommodation means class sets the amount',
    );
  }
  return normalized;
}

/**
 * D7 accommodation amount for one priced day. No contribution input, or
 * ACCOMMODATION_PRICE, keeps the supplied accommodation payment unchanged.
 */
function resolveAccommodationCents(
  suppliedAccommodationCents: number,
  contribution: NormalizedAccommodationContribution | null,
): { cents: number; hold: boolean; reasons: ResidentialFeeReasonCode[] } {
  if (contribution === null || contribution.meansClass === 'ACCOMMODATION_PRICE') {
    return { cents: suppliedAccommodationCents, hold: false, reasons: [] };
  }
  if (contribution.meansClass === 'FULLY_SUPPORTED') {
    return { cents: 0, hold: false, reasons: ['ACCOMMODATION_FULLY_SUPPORTED'] };
  }
  const reasons: ResidentialFeeReasonCode[] = ['ACCOMMODATION_LOW_MEANS_CONTRIBUTION'];
  if (contribution.advisedMaxDailyCents === null) {
    reasons.push('ACCOMMODATION_CONTRIBUTION_MAX_MISSING');
    return { cents: 0, hold: true, reasons };
  }
  if (contribution.refundableBalanceCents > 0) {
    reasons.push('ACCOMMODATION_RAC_BALANCE_UNRESOLVED');
    return { cents: 0, hold: true, reasons };
  }
  const maxCents = contribution.advisedMaxDailyCents;
  const agreedCents = contribution.agreedDailyCents;
  if (agreedCents !== null && agreedCents > maxCents) {
    reasons.push('ACCOMMODATION_CONTRIBUTION_CAPPED_AT_ADVISED_MAX');
    return { cents: maxCents, hold: false, reasons };
  }
  return { cents: agreedCents ?? maxCents, hold: false, reasons };
}

function contributionAfterReduction(args: {
  cohort: ResidentialFeeCohort;
  bdfCents: number;
  hotellingCents: number;
  ncccCents: number;
  mtcfCents: number;
  accommodationCents: number;
  feeReductionCents: number;
}): number {
  const bdf = component(args.bdfCents, 'bdf');
  const hotelling = component(args.hotellingCents, 'hotelling');
  const nccc = component(args.ncccCents, 'nccc');
  const mtcf = component(args.mtcfCents, 'mtcf');
  const accommodation = component(args.accommodationCents, 'accommodation');
  const order =
    args.cohort === 'MTCF_POST_2014'
      ? [bdf, mtcf, accommodation]
      : [bdf, hotelling, nccc, accommodation];
  applyFeeReduction(order, args.feeReductionCents);
  return bdf.netCents + hotelling.netCents + nccc.netCents + mtcf.netCents;
}

function priceBedReservation(args: {
  cohort: ResidentialFeeCohort;
  rateRow: ResidentialFeeRatePeriodV3;
  bedReservation: ResidentialBedReservationInput | null;
  /** Resident contribution after the r 231-25 / r 242B-27 waterfall. Not the reservation fee. */
  hypotheticalContributionCents: number;
}): { feeCents: number; maxCents: number | null; agreed: boolean; missingBaseRate: boolean } {
  const agreedCents = args.bedReservation?.agreedDailyCents ?? null;
  const agreed = agreedCents !== null;
  if (args.bedReservation?.standardBaseProviderAmountCents !== undefined) {
    nonNegativeCents(
      'standardBaseProviderAmountCents',
      args.bedReservation.standardBaseProviderAmountCents,
    );
  }
  if (agreedCents !== null) nonNegativeCents('agreedDailyCents', agreedCents);
  if (args.cohort === 'MTCF_POST_2014' && args.bedReservation?.baseRateAsIfCents === undefined) {
    return { feeCents: 0, maxCents: null, agreed, missingBaseRate: true };
  }
  const override = args.bedReservation?.standardBaseProviderAmountCents;
  const bpaCents =
    override === undefined
      ? args.rateRow.standardBaseProviderAmountCents
      : nonNegativeCents('standardBaseProviderAmountCents', override);
  const baseRateCents =
    args.cohort === 'MTCF_POST_2014'
      ? nonNegativeCents('baseRateAsIfCents', args.bedReservation?.baseRateAsIfCents ?? 0)
      : 0;
  const maxCents = args.hypotheticalContributionCents + bpaCents + baseRateCents;
  if (!agreed || agreedCents === null) {
    return { feeCents: 0, maxCents, agreed: false, missingBaseRate: false };
  }
  const agreedDailyCents = nonNegativeCents('agreedDailyCents', agreedCents);
  return {
    feeCents: agreedDailyCents < maxCents ? agreedDailyCents : maxCents,
    maxCents,
    agreed: true,
    missingBaseRate: false,
  };
}

function applyFeeReduction(
  order: ResidentialFeeComponentCents[],
  availableCents: number,
): { appliedCents: number; unappliedCents: number } {
  let remainingCents = availableCents;
  let appliedCents = 0;
  for (const part of order) {
    const takeCents = remainingCents < part.grossCents ? remainingCents : part.grossCents;
    part.reductionCents = takeCents;
    part.netCents = part.grossCents - takeCents;
    remainingCents -= takeCents;
    appliedCents += takeCents;
  }
  return { appliedCents, unappliedCents: remainingCents };
}

function resolveAnnualPeriod(
  cohort: ResidentialFeeCohort,
  firstDay: string | null,
  date: string,
  positions: ResidentialFeePositions,
): {
  annualBeforeCents: number;
  periodStart: string | null;
  capApplies: boolean;
  ended: boolean;
  unresolvable: boolean;
} {
  const supplied = {
    annualBeforeCents: positions.mtcfAnnualTotalCents,
    periodStart: positions.mtcfAnnualPeriodStart,
    ended: false,
    unresolvable: false,
    capApplies: false,
  };
  if (cohort !== 'MTCF_POST_2014') return supplied;
  // Every statutory annual period has ended. Do not hold and do not cap.
  if (date > MTCF_ANNUAL_CAP_LAST_DAY) {
    return { ...supplied, ended: true };
  }
  if (firstDay !== null) {
    const resolved = resolveMtcfAnnualCapPeriod(firstDay);
    if (resolved.status === 'unresolvable') {
      return { ...supplied, capApplies: false, unresolvable: true };
    }
    if (resolved.status === 'none') {
      return { ...supplied, periodStart: null, capApplies: false };
    }
    const inPeriod = date >= resolved.start && date <= resolved.endInclusive;
    return {
      annualBeforeCents: positions.mtcfAnnualTotalCents,
      periodStart: resolved.start,
      capApplies: inPeriod,
      ended: date > resolved.endInclusive,
      unresolvable: false,
    };
  }
  if (positions.mtcfAnnualPeriodStart !== null) {
    const start = positions.mtcfAnnualPeriodStart;
    const endInclusive = previousYmd(addCalendarMonths(start, 12));
    const inPeriod = date >= start && date <= endInclusive && date <= MTCF_ANNUAL_CAP_LAST_DAY;
    return {
      annualBeforeCents: positions.mtcfAnnualTotalCents,
      periodStart: start,
      capApplies: inPeriod,
      ended: date > endInclusive,
      unresolvable: false,
    };
  }
  const couldBeInsideAPeriod = date >= MTCF_ANNUAL_CAP_WINDOW_START;
  return {
    ...supplied,
    capApplies: false,
    unresolvable: couldBeInsideAPeriod,
  };
}

function resolveProviderInformed(
  means: ResidentialFeeMeans,
  policy: ResidentialFeePolicyV3,
): { date: string | null; assumed: boolean } {
  if (means.providerInformedOn !== null) return { date: means.providerInformedOn, assumed: false };
  if (policy.assumeProviderInformedFromSourceIssuedAt && means.sourceIssuedAt !== null) {
    return { date: means.sourceIssuedAt, assumed: true };
  }
  return { date: null, assumed: false };
}

function meansTestedChargeable(
  state: ResidentialDayState,
  policy: ResidentialFeePolicyV3,
): boolean {
  if (state === 'BED_RESERVATION') return false;
  if (state === 'DEPARTURE') return policy.departureDayMeansTested === 'CHARGED';
  return true;
}

function addDayStateReason(
  reasons: Set<ResidentialFeeReasonCode>,
  state: ResidentialDayState,
): void {
  switch (state) {
    case 'ENTRY':
      reasons.add('DAY_ENTRY');
      return;
    case 'DEPARTURE':
      reasons.add('DAY_DEPARTURE');
      return;
    case 'RETURN':
      reasons.add('DAY_RETURN_NOT_LEAVE');
      return;
    case 'LEAVE_SOCIAL':
      reasons.add('LEAVE_SOCIAL');
      return;
    case 'LEAVE_HOSPITAL':
      reasons.add('LEAVE_HOSPITAL');
      return;
    case 'BED_RESERVATION':
      reasons.add('SOCIAL_LEAVE_LIMIT_BED_RESERVATION');
      return;
    case 'IN_CARE':
      return;
    default: {
      const unreachable: never = state;
      return unreachable;
    }
  }
}

function ceasedBeforeProviderInformed(
  ceasedOn: string | null,
  providerInformedOn: string | null,
): boolean {
  if (ceasedOn === null) return false;
  if (providerInformedOn === null) return true;
  return providerInformedOn > ceasedOn;
}

/** Rules r 280-5(a)(iii). Literal: once it applies, hotelling and NCCC are zero for the whole stay. */
function ncccInformedAfterSixMonths(stayStart: string | null, informedOn: string | null): boolean {
  if (stayStart === null || informedOn === null) return false;
  return informedOn > addCalendarMonths(stayStart, 6);
}

/**
 * Rules r 285A-14(7). The applicable day is the earlier of the stay start and
 * the old-Act day of entry. MTCF is zero from that day until the day the
 * individual is informed of the means tested care fee, and only when that
 * information arrived more than six months after the applicable day. A missing
 * informed date does not itself zero the fee; the caller holds instead.
 */
export function mtcfLateInformationApplies(
  date: string,
  applicableDay: string | null,
  informedOn: string | null,
): boolean {
  if (applicableDay === null || informedOn === null || date < applicableDay) return false;
  const deadline = addCalendarMonths(applicableDay, 6);
  if (informedOn <= deadline) return false;
  return date < informedOn;
}

function earlierPresentDay(left: string | null, right: string | null): string | null {
  if (left === null) return right;
  if (right === null) return left;
  return left < right ? left : right;
}

/**
 * `inactive` when the caller has not raised r 285A-14(7). `unresolvable` when
 * an old-Act entry or an MTCF informed date is present but the pair cannot
 * be completed. `zero` when the exemption applies on this day.
 */
function resolveMtcfLateInformation(
  date: string,
  means: ResidentialFeeMeans,
): 'inactive' | 'zero' | 'not-zero' | 'unresolvable' {
  const raised = means.mtcfInformedOn !== null || means.oldActDayOfEntry !== null;
  if (!raised) return 'inactive';
  const applicableDay = earlierPresentDay(means.stayStartDay, means.oldActDayOfEntry);
  if (applicableDay === null || means.mtcfInformedOn === null) return 'unresolvable';
  return mtcfLateInformationApplies(date, applicableDay, means.mtcfInformedOn)
    ? 'zero'
    : 'not-zero';
}

function nextLifetimeCapExceededOn(args: {
  openOn: string | null;
  openTotalCents: number;
  closeTotalCents: number;
  capCents: number;
  date: string;
}): string | null {
  if (args.openOn !== null) return args.openOn;
  if (args.openTotalCents > args.capCents) return previousYmd(args.date);
  if (args.closeTotalCents > args.capCents) return args.date;
  return null;
}

function rateWindowCovers(row: ResidentialFeeRatePeriodV3, date: string): boolean {
  return date >= row.effectiveFrom && (row.effectiveTo === null || date <= row.effectiveTo);
}

interface ResolvedCaps {
  ncccLifetimeCapCents: number;
  mtcfAnnualCapCents: number;
  mtcfLifetimeCapCents: number;
  ncccTimeLimitDays: number;
}

function resolveCaps(
  rateRow: ResidentialFeeRatePeriodV3,
  caps?: ResidentialFeeCapOverrides | null,
): ResolvedCaps {
  return {
    ncccLifetimeCapCents: nonNegativeCents(
      'ncccLifetimeCapCents',
      caps?.ncccLifetimeCapCents ?? rateRow.ncccLifetimeCapCents,
    ),
    mtcfAnnualCapCents: nonNegativeCents(
      'mtcfAnnualCapCents',
      caps?.mtcfAnnualCapCents ?? rateRow.mtcfAnnualCapCents,
    ),
    mtcfLifetimeCapCents: nonNegativeCents(
      'mtcfLifetimeCapCents',
      caps?.mtcfLifetimeCapCents ?? rateRow.mtcfLifetimeCapCents,
    ),
    ncccTimeLimitDays: nonNegativeCount(
      'ncccTimeLimitDays',
      caps?.ncccTimeLimitDays ?? NCCC_TIME_LIMIT_DAYS,
    ),
  };
}

function unsupportedPre2014(args: {
  date: string;
  dayState: ResidentialDayState;
  socialLeaveOrdinalFy: number | null;
  hospitalEpisodeDay: number | null;
  positions: ResidentialFeePositions;
  feeReductionCents: number;
  rates: ResidentialFeeRatePeriodV3 | null;
}): ResidentialFeeDayResult {
  if (args.rates !== null) assertResidentialFeeRatePeriod(args.rates);
  const reasons: ResidentialFeeReasonCode[] = ['UNSUPPORTED_PRE_2014'];
  if (args.rates === null) reasons.push('RATE_PERIOD_MISSING');
  if (args.feeReductionCents > 0) reasons.push('FEE_REDUCTION_UNAPPLIED_REMAINDER');
  const zero = (kind: keyof typeof RESIDENTIAL_COMPONENT_GST) => component(0, kind);
  return {
    date: args.date,
    dayState: args.dayState,
    cohort: 'ITF_PRE_2014',
    calcVersion: RAC_DAY_CALC_VERSION,
    ratePeriodId: args.rates?.id ?? null,
    supported: false,
    hold: true,
    retroBillable: false,
    socialLeaveOrdinalFy: args.socialLeaveOrdinalFy,
    hospitalEpisodeDay: args.hospitalEpisodeDay,
    bdf: zero('bdf'),
    hotelling: zero('hotelling'),
    nccc: zero('nccc'),
    mtcf: zero('mtcf'),
    itf: zero('itf'),
    accommodation: zero('accommodation'),
    bedReservation: zero('bedReservation'),
    helf: zero('helf'),
    maxBedReservationCents: null,
    feeReductionAvailableCents: args.feeReductionCents,
    feeReductionAppliedCents: 0,
    feeReductionUnappliedCents: args.feeReductionCents,
    ncccDayIncrement: 0,
    sharedLifetimeIncrementCents: 0,
    mtcfAnnualIncrementCents: 0,
    mtcfLifetimeIncrementCents: 0,
    mtcfAnnualPeriodStart: args.positions.mtcfAnnualPeriodStart,
    positionsOpen: args.positions,
    positionsClose: { ...args.positions },
    reasonCodes: orderResidentialFeeReasonCodes(reasons),
    careNetCents: 0,
    grossDailyTotalCents: 0,
  };
}

function component(
  grossCents: number,
  kind: keyof typeof RESIDENTIAL_COMPONENT_GST,
): ResidentialFeeComponentCents {
  return {
    grossCents,
    reductionCents: 0,
    netCents: grossCents,
    gstTreatment: RESIDENTIAL_COMPONENT_GST[kind],
  };
}

function requiredCost(means: ResidentialFeeMeans): number {
  if (means.residentCostOfCareCents === null) {
    throw new RangeError('MTCF resident cost of care is required');
  }
  return means.residentCostOfCareCents;
}

function normalizePositions(
  input?: Partial<ResidentialFeePositions> | null,
): ResidentialFeePositions {
  const positions: ResidentialFeePositions = {
    ...emptyResidentialFeePositions(),
    ...input,
    mtcfAnnualPeriodStart: input?.mtcfAnnualPeriodStart ?? null,
    sharedLifetimeCapExceededOn: input?.sharedLifetimeCapExceededOn ?? null,
  };
  nonNegativeCents('sharedLifetimeTotalCents', positions.sharedLifetimeTotalCents);
  nonNegativeCents('mtcfAnnualTotalCents', positions.mtcfAnnualTotalCents);
  nonNegativeCents('mtcfLifetimeTotalCents', positions.mtcfLifetimeTotalCents);
  nonNegativeCount('ncccDays', positions.ncccDays);
  if (positions.mtcfAnnualPeriodStart !== null) {
    assertResidentialFeeDate(positions.mtcfAnnualPeriodStart);
  }
  if (positions.sharedLifetimeCapExceededOn !== null) {
    assertResidentialFeeDate(positions.sharedLifetimeCapExceededOn);
  }
  return positions;
}

function optionalDate(name: string, value: string | null | undefined): string | null {
  if (value == null) return null;
  assertResidentialFeeDate(value);
  if (!value) throw new RangeError(`${name} is blank`);
  return value;
}

function optionalCents(name: string, value: number | null): void {
  if (value === null) return;
  nonNegativeCents(name, value);
}

function nonNegativeCents(name: string, value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative safe integer number of cents`);
  }
  return value;
}

function nonNegativeCount(name: string, value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function anniversaryInsideAnnualWindow(firstDay: string): string | null {
  const startYear = Number(MTCF_ANNUAL_CAP_WINDOW_START.slice(0, 4));
  const endYear = Number(MTCF_ANNUAL_CAP_WINDOW_END.slice(0, 4));
  const found: string[] = [];
  for (let year = startYear; year <= endYear; year += 1) {
    const anniversary = anniversaryOn(firstDay, year);
    if (
      anniversary !== null &&
      anniversary >= firstDay &&
      anniversary >= MTCF_ANNUAL_CAP_WINDOW_START &&
      anniversary <= MTCF_ANNUAL_CAP_WINDOW_END
    ) {
      found.push(anniversary);
    }
  }
  return found.length === 1 ? found[0]! : null;
}

/** No fallback when the calendar day does not exist. A 29 February anniversary in 2025 is null. */
function anniversaryOn(firstDay: string, year: number): string | null {
  const month = Number(firstDay.slice(5, 7));
  const day = Number(firstDay.slice(8, 10));
  if (day > daysInMonth(year, month)) return null;
  return formatYmd(year, month, day);
}

function ratePeriodRejected(args: {
  date: string;
  dayState: ResidentialDayState;
  cohort: ResidentialFeeCohort;
  socialLeaveOrdinalFy: number | null;
  hospitalEpisodeDay: number | null;
  positions: ResidentialFeePositions;
  feeReductionCents: number;
  ratePeriodId: string;
  supported: boolean;
}): ResidentialFeeDayResult {
  const held = ratePeriodMissing({ ...args, cohort: args.cohort });
  const reasons = new Set<ResidentialFeeReasonCode>(held.reasonCodes);
  reasons.delete('RATE_PERIOD_MISSING');
  reasons.add('RATE_PERIOD_MISMATCH');
  if (!args.supported) reasons.add('UNSUPPORTED_PRE_2014');
  return {
    ...held,
    cohort: args.cohort,
    supported: args.supported,
    ratePeriodId: args.ratePeriodId,
    reasonCodes: orderResidentialFeeReasonCodes(reasons),
  };
}

function ratePeriodMissing(args: {
  date: string;
  dayState: ResidentialDayState;
  cohort: ResidentialFeeCohort;
  socialLeaveOrdinalFy: number | null;
  hospitalEpisodeDay: number | null;
  positions: ResidentialFeePositions;
  feeReductionCents: number;
}): ResidentialFeeDayResult {
  const reasons = new Set<ResidentialFeeReasonCode>(['RATE_PERIOD_MISSING']);
  addDayStateReason(reasons, args.dayState);
  if (args.feeReductionCents > 0) reasons.add('FEE_REDUCTION_UNAPPLIED_REMAINDER');
  const zero = (kind: keyof typeof RESIDENTIAL_COMPONENT_GST) => component(0, kind);
  return {
    date: args.date,
    dayState: args.dayState,
    cohort: args.cohort,
    calcVersion: RAC_DAY_CALC_VERSION,
    ratePeriodId: null,
    supported: true,
    hold: true,
    retroBillable: false,
    socialLeaveOrdinalFy: args.socialLeaveOrdinalFy,
    hospitalEpisodeDay: args.hospitalEpisodeDay,
    bdf: zero('bdf'),
    hotelling: zero('hotelling'),
    nccc: zero('nccc'),
    mtcf: zero('mtcf'),
    itf: zero('itf'),
    accommodation: zero('accommodation'),
    bedReservation: zero('bedReservation'),
    helf: zero('helf'),
    maxBedReservationCents: null,
    feeReductionAvailableCents: args.feeReductionCents,
    feeReductionAppliedCents: 0,
    feeReductionUnappliedCents: args.feeReductionCents,
    ncccDayIncrement: 0,
    sharedLifetimeIncrementCents: 0,
    mtcfAnnualIncrementCents: 0,
    mtcfLifetimeIncrementCents: 0,
    mtcfAnnualPeriodStart: args.positions.mtcfAnnualPeriodStart,
    positionsOpen: args.positions,
    positionsClose: { ...args.positions },
    reasonCodes: orderResidentialFeeReasonCodes(reasons),
    careNetCents: 0,
    grossDailyTotalCents: 0,
  };
}

function assertCohort(cohort: ResidentialFeeCohort): void {
  if (!RESIDENTIAL_FEE_COHORTS.includes(cohort)) {
    throw new RangeError(`Invalid residential fee cohort: ${String(cohort)}`);
  }
}

function assertDayState(state: ResidentialDayState): void {
  if (!RESIDENTIAL_DAY_STATES.includes(state)) {
    throw new RangeError(`Invalid residential day state: ${String(state)}`);
  }
}
